import React, { useState, useEffect, useRef } from "react";
import Tooltip from "../../../Shared/Tootip";
import ReportsStore from "./ReportsStore";
import { t } from "../../../I18n/i18n";

const SelectTemplate = ({ reports, selectedTemplate, addTemplate, editTemplateName, deleteTemplate, importTemplate, onChoose }) => {
    const [newTemplateName, setNewTemplateName] = useState("");
    const [editTemplateNameState, setEditTemplateNameState] = useState("");
    const [selectedTemplateState, setSelectedTemplateState] = useState({});
    const addModalRef = useRef(null);
    const editModalRef = useRef(null);
    const deleteModalRef = useRef(null);
    const selectRef = useRef(null);

    useEffect(() => {
        setSelectedTemplateState(selectedTemplate);
        setEditTemplateNameState(selectedTemplate?.name || "");
        ReportsStore.selectedTemplateId = selectedTemplate?.id;

        if (addModalRef.current) M.Modal.init(addModalRef.current, {});
        if (editModalRef.current) M.Modal.init(editModalRef.current, {});
        if (deleteModalRef.current) M.Modal.init(deleteModalRef.current, {});

        return () => {
            if(addModalRef.current) M.Modal.getInstance(addModalRef.current)?.destroy();
            if(editModalRef.current) M.Modal.getInstance(editModalRef.current)?.destroy();
            if(deleteModalRef.current) M.Modal.getInstance(deleteModalRef.current)?.destroy();
            if(selectRef.current) M.FormSelect.getInstance(selectRef.current)?.destroy();
        };
    }, []);

    useEffect(() => {
        setSelectedTemplateState(selectedTemplate);
        setEditTemplateNameState(selectedTemplate?.name || "");
        if (selectRef.current) {
            M.FormSelect.init(selectRef.current, {});
        }
        ReportsStore.selectedTemplateId = selectedTemplate?.id;
    }, [selectedTemplate]);

    const exportTemplate = (template) => {
        const dataStr = JSON.stringify({ ...template, id: undefined });
        const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
        const exportFileDefaultName = `rapport-${template.name}.json`;
        const linkElement = document.createElement("a");
        linkElement.setAttribute("href", dataUri);
        linkElement.setAttribute("download", exportFileDefaultName);
        linkElement.click();
    };

    const handleImport = (event) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const template = JSON.parse(e.target.result);
            importTemplate({ ...template, id: undefined });
        };
        reader.readAsText(event.target.files[0]);
        M.Modal.getInstance(addModalRef.current)?.close();
    };

    const selectTemplate = (event) => {
        const selected = reports.templates[event.target.value];
        setSelectedTemplateState(selected);
        setEditTemplateNameState(selected.name);
        onChoose(selected);
    };

    const hasTemplates = () => {
        return !(!reports || !reports.templates || reports.templates.length === 0);
    };

    const renderSelect = (templates) => {
        if (templates.length === 0) {
            return <div className={`${templates.length === 0 ? "" : "hide"}`}>{t("SELECT_TEMPLATE.NO_EXISTING_REPORT")}</div>;
        }
        return (
            <div className="valign-wrapper" style={{ gap: 10 }}>
                <select
                    id="template_select"
                    ref={selectRef}
                    className="browser-default"
                    onChange={selectTemplate}
                >
                    {templates.map((template, index) => (
                        <option key={index} value={index}>
                            {template.name}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    const templates = reports?.templates || [];

    return (
        <div style={{ boxShadow: "0px 3px 0px 0px rgba(0, 0, 0, 0.10)", marginBottom: "3px", padding: "10px 0" }}>
            <div className="row valign-wrapper" style={{ marginBottom: "0", padding: "0 1vw 0 1vw" }}>
                <div className="col s5">{t("SELECT_TEMPLATE.TYPE_OF_REPORT")}</div>
                <div className="col s7 valign-wrapper" style={{ display: "flex" }}>
                    <div style={{ maxWidth: "75%" }}>{renderSelect(templates)}</div>
                    <div style={{ marginLeft: "auto", paddingLeft: "1vw" }} className="valign-wrapper">
                        <Tooltip id="create_new_template" text={t("SELECT_TEMPLATE.NEW_REPORT")}>
                            <a
                                className="btn waves-light btn-small z-depth-0 transparent blue-text darken-2 modal-trigger"
                                data-target="add_template_modal"
                                style={{ borderRadius: "2px 0 0 2px", padding: "0 7px 0 7px" }}
                            >
                                <i className="material-icons">add</i>
                            </a>
                        </Tooltip>
                        <Tooltip id="edit_template" text={t("SELECT_TEMPLATE.RENAME_REPORT")}>
                            <a
                                className={`btn waves-light btn-small z-depth-0 transparent ${
                                    hasTemplates() ? "orange-text darken-2 modal-trigger" : "grey-text"
                                }`}
                                data-target="edit_template_modal"
                                style={{ borderRadius: "0", padding: "0 7px 0 7px" }}
                            >
                                <i className="material-icons">edit</i>
                            </a>
                        </Tooltip>
                        <Tooltip id="delete_template" text={t("SELECT_TEMPLATE.DELETE_REPORT")}>
                            <a
                                className={`btn waves-light btn-small z-depth-0 transparent ${
                                    hasTemplates() ? "red-text darken-2 modal-trigger" : "grey-text"
                                }`}
                                data-target="delete_template_modal"
                                style={{ borderRadius: "0 2px 2px 0", padding: "0 7px 0 7px" }}
                            >
                                <i className="material-icons">delete_forever</i>
                            </a>
                        </Tooltip>
                        <Tooltip id="download_template" text={t("SELECT_TEMPLATE.DOWNLOAD_REPORT")}>
                            <a
                                className={`btn waves-light btn-small z-depth-0 transparent ${
                                    hasTemplates() ? "green-text darken-2 modal-trigger" : "grey-text"
                                }`}
                                onClick={() => exportTemplate(selectedTemplateState)}
                                style={{ borderRadius: "0 2px 2px 0", padding: "0 7px 0 7px" }}
                            >
                                <i className="material-icons">download</i>
                            </a>
                        </Tooltip>
                    </div>
                </div>
            </div>

            {/* CREATE MODAL */}
            <div
                id="add_template_modal"
                ref={addModalRef}
                className="modal"
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        addTemplate(newTemplateName);
                        M.Modal.getInstance(addModalRef.current)?.close();
                    }
                }}
            >
                <div className="modal-content">
                    <h5>{t("SELECT_TEMPLATE.CREATE_NEW_REPORT")}</h5>
                    <div className="valign-wrapper row">
                        <div className="col s2">
                            <h5>{t("LASTNAME")} :</h5>
                        </div>
                        <div className="col s10">
                            <input
                                type="text"
                                onChange={(e) => setNewTemplateName(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="modal-footer valign-wrapper" style={{ justifyContent: "center" }}>
                    <div style={{ padding: "0 1vw" }}>
                        <a
                            className="modal-close btn blue darken-2 z-depth-0"
                            onClick={() => addTemplate(newTemplateName)}
                        >
                            {t("CREATE")}
                        </a>
                    </div>
                    <div style={{ padding: "0 1vw" }}>
                        <span className="btn btn-file green darken-2 z-depth-0">
                            Importer
                            <input type="file" accept=".json" onInput={handleImport} />
                        </span>
                    </div>
                    <div style={{ padding: "0 1vw" }}>
                        <a className="modal-close btn red z-depth-0">{t("CANCEL")}</a>
                    </div>
                </div>
            </div>

            {/* EDIT MODAL */}
            <div
                id="edit_template_modal"
                ref={editModalRef}
                className="modal"
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        editTemplateName(editTemplateNameState);
                        M.Modal.getInstance(editModalRef.current)?.close();
                    }
                }}
            >
                <div className="modal-content">
                    <h5>{t("SELECT_TEMPLATE.CHANGE_NAME_REPORT")}</h5>
                    <div className="valign-wrapper row">
                        <div className="col s2">
                            <h5>{t("LASTNAME")} :</h5>
                        </div>
                        <div className="col s10" style={{ marginLeft: 0 }}>
                            <input
                                type="text"
                                defaultValue={editTemplateNameState}
                                style={{ maxWidth: "80%" }}
                                onChange={(e) => setEditTemplateNameState(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="modal-footer valign-wrapper" style={{ justifyContent: "center" }}>
                    <a
                        className="modal-close btn blue darken-2 z-depth-0"
                        onClick={() => editTemplateName(editTemplateNameState)}
                    >
                        {t("EDIT")}
                    </a>
                    <div style={{ paddingLeft: "3vw" }}>
                        <a className="modal-close btn red z-depth-0">{t("CANCEL")}</a>
                    </div>
                </div>
            </div>

            {/* DELETE MODAL */}
            <div id="delete_template_modal" ref={deleteModalRef} className="modal">
                <div className="modal-content">{t("SELECT_TEMPLATE.DELETE_THIS_REPORT")}</div>
                <div className="modal-footer valign-wrapper" style={{ justifyContent: "center" }}>
                    <a className="modal-close btn red z-depth-0" onClick={deleteTemplate}>
                        {t("DELETE")}
                    </a>
                    <div style={{ paddingLeft: "3vw" }}>
                        <a className="modal-close btn black z-depth-0">{t("CANCEL")}</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

SelectTemplate.defaultProps = {
    reports: { templates: [] },
};

export default SelectTemplate;
